import { shallowRef } from "vue";
import { defineStore } from "pinia";
import type {
  BriefSolution,
  DraftValidation,
  Project,
  ProjectTimeline,
} from "@/types";
import { v2 } from "@/services/v2";

export const useProjectStore = defineStore("projects", () => {
  const active = shallowRef<Project | null>(null);
  const solution = shallowRef<BriefSolution | null>(null);
  const validation = shallowRef<DraftValidation>({ valid: false, errors: [] });
  const timeline = shallowRef<ProjectTimeline | null>(null);
  const dashboard = shallowRef<Awaited<ReturnType<typeof v2.dashboard>> | null>(null);
  const teamQueue = shallowRef<Awaited<ReturnType<typeof v2.teamProjects>> | null>(null);
  const loading = shallowRef(false);

  function clearBriefContext() {
    active.value = null;
    solution.value = null;
    validation.value = { valid: false, errors: [] };
    timeline.value = null;
  }

  async function loadSolution(briefId: number) {
    solution.value = (await v2.currentSolution(briefId)).solution;
  }
  async function confirmCurrentSolution(briefId: number) {
    if (!solution.value) return;
    const result = await v2.confirmSolution(briefId, solution.value.id);
    solution.value = result.solution;
    active.value = result.project;
    await loadDraft(briefId);
  }
  async function loadDraft(briefId: number) {
    const result = await v2.draft(briefId);
    active.value = result.project;
    validation.value = result.validation;
  }
  async function reloadValidation() {
    if (active.value) await loadDraft(active.value.id);
  }
  async function updateDraft(body: unknown) {
    if (!active.value) return;
    active.value = (await v2.updateDraft(active.value.id, body)).project;
    await reloadValidation();
  }
  async function addTask(body: unknown) {
    if (!active.value) return;
    active.value = (await v2.addTask(active.value.id, body)).project;
    await reloadValidation();
  }
  async function updateTask(taskId: number, body: unknown) {
    if (!active.value) return;
    active.value = (await v2.updateTask(active.value.id, taskId, body)).project;
    await reloadValidation();
  }
  async function deleteTask(taskId: number) {
    if (!active.value) return;
    active.value = (await v2.deleteTask(active.value.id, taskId)).project;
    await reloadValidation();
  }
  async function addChecklist(taskId: number, body: unknown) {
    active.value = (await v2.addChecklist(taskId, body)).project;
    await reloadValidation();
  }
  async function updateChecklist(itemId: number, body: unknown) {
    active.value = (await v2.updateChecklist(itemId, body)).project;
    await reloadValidation();
  }
  async function deleteChecklist(itemId: number) {
    active.value = (await v2.deleteChecklist(itemId)).project;
    await reloadValidation();
  }
  async function submit() {
    if (!active.value) return;
    active.value = (await v2.submit(active.value.id)).project;
    await loadDashboard();
  }
  async function loadDashboard() {
    dashboard.value = await v2.dashboard();
  }
  async function loadTimeline(briefId: number) {
    timeline.value = (await v2.timeline(briefId)).timeline;
  }
  async function loadTeamQueue() {
    teamQueue.value = await v2.teamProjects();
  }
  async function loadTeamProject(briefId: number) {
    const result = await v2.teamProject(briefId);
    active.value = result.project;
    timeline.value = result.timeline;
  }
  async function acceptProject() {
    if (!active.value) return;
    active.value = (await v2.acceptProject(active.value.id)).project;
    await loadTeamQueue();
  }
  async function scheduleTask(taskId: number, body: unknown) {
    active.value = (await v2.scheduleTask(taskId, body)).project;
    await loadTeamQueue();
  }
  async function updateChecklistStatus(itemId: number, body: unknown) {
    active.value = (await v2.updateChecklistStatus(itemId, body)).project;
    if (active.value) await loadTimeline(active.value.id);
    await loadTeamQueue();
  }

  return {
    active,
    solution,
    validation,
    timeline,
    dashboard,
    teamQueue,
    loading,
    clearBriefContext,
    loadSolution,
    confirmCurrentSolution,
    loadDraft,
    updateDraft,
    addTask,
    updateTask,
    deleteTask,
    addChecklist,
    updateChecklist,
    deleteChecklist,
    submit,
    loadDashboard,
    loadTimeline,
    loadTeamQueue,
    loadTeamProject,
    acceptProject,
    scheduleTask,
    updateChecklistStatus,
  };
});
